import React, { useState, useEffect, useContext } from "react";
import { Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Home,
  Heart,
  Trophy,
  Users,
  CheckCircle,
  ShoppingBag,
  Wallet,
  Shield,
  Settings,
  LogOut,
  User, BarChart3 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Separator } from "../components/ui/separator";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import AuthContext from "../context/AuthContext";
import OrgThemeContext from "../context/OrgThemeContext";
import { notificationsService } from "../services/notificationsService";
import UserAvatar from "./UserAvatar";
import { useTranslation } from "../i18n";

const Sidebar = ({ onOpenForm, isExpanded, toggleSidebar, topBarHeight = 48 }) => {
  const { t } = useTranslation("nav");
  const location = useLocation();
  const { user, logout } = useContext(AuthContext);
  const orgTheme = useContext(OrgThemeContext);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  
  // Track window size for responsive behavior
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-collapse sidebar when window resizes below mobile breakpoint
  const prevWidthRef = React.useRef(windowWidth);
  useEffect(() => {
    const wasAboveMobile = prevWidthRef.current >= 768;
    const isBelowMobile = windowWidth < 768;
    if (wasAboveMobile && isBelowMobile && isExpanded) {
      toggleSidebar(false);
    }
    prevWidthRef.current = windowWidth;
  }, [windowWidth, isExpanded, toggleSidebar]);

  // Delay tooltip enabling until after the collapse animation (300ms) so labels
  // don't flash during the width transition. After settling, briefly force-open
  // the tooltip for the active page so the user sees where they are.
  const [tooltipsEnabled, setTooltipsEnabled] = useState(false);
  const [forcedLabel, setForcedLabel] = useState(null);
  useEffect(() => {
    if (isExpanded) {
      setTooltipsEnabled(false);
      setForcedLabel(null);
      return;
    }
    const activeLabel = (() => {
      const activePaths = [
        { path: "/", label: t("sidebar.feed") },
        { path: "/recognition", label: t("sidebar.send_recognition") },
        { path: "/nominations", label: t("sidebar.nominations") },
        { path: "/teams", label: t("sidebar.teams") },
        { path: "/approvals", label: t("sidebar.approvals") },
        { path: "/marketplace", label: t("sidebar.marketplace") },
        { path: "/wallet", label: t("sidebar.wallet") },
        { path: "/insights", label: t("sidebar.insights") },
        { path: "/admin", label: t("sidebar.admin") },
      ];
      const match = activePaths.find(p =>
        p.path === "/" ? location.pathname === "/" : location.pathname.startsWith(p.path)
      );
      return match?.label || null;
    })();

    const enableTimer = setTimeout(() => {
      if (activeLabel) setForcedLabel(activeLabel);
    }, 300);
    const clearTimer = setTimeout(() => {
      setForcedLabel(null);
      setTooltipsEnabled(true);
    }, 1200);
    return () => {
      clearTimeout(enableTimer);
      clearTimeout(clearTimer);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded]);

  const isApprover = user?.is_superuser || user?.role === "admin" || user?.is_team_admin;

  const { data: pendingApprovalsData } = useQuery({
    queryKey: ["pending-approvals-count"],
    queryFn: notificationsService.getPendingApprovalsCount,
    refetchInterval: 30000,
    refetchIntervalInBackground: true,
    enabled: !!isApprover,
  });

  const pendingApprovalsCount = pendingApprovalsData?.count || 0;

  const menuItems = [
    { path: "/",             icon: <Home size={20} />,        label: t("sidebar.feed"),             description: t("sidebar.feed_description") },
    { path: "/recognition",  icon: <Heart size={20} />,       label: t("sidebar.send_recognition"), description: t("sidebar.send_recognition_description") },
    { path: "/nominations",  icon: <Trophy size={20} />,      label: t("sidebar.nominations"),      description: t("sidebar.nominations_description") },
    { path: "/teams",        icon: <Users size={20} />,       label: t("sidebar.teams"),            description: t("sidebar.teams_description") },
    ...(isApprover
      ? [{ path: "/approvals", icon: <CheckCircle size={20} />, label: t("sidebar.approvals"), description: t("sidebar.approvals_description"), badge: pendingApprovalsCount }]
      : []),
    { path: "/marketplace",  icon: <ShoppingBag size={20} />, label: t("sidebar.marketplace"),      description: t("sidebar.marketplace_description") },
    { path: "/wallet",       icon: <Wallet size={20} />,      label: t("sidebar.wallet"),           description: t("sidebar.wallet_description") },
    { path: "/insights",     icon: <BarChart3 size={20} />,   label: t("sidebar.insights"),         description: t("sidebar.insights_description") },
    ...(user?.is_superuser || user?.role === "admin"
      ? [{ path: "/admin",     icon: <Shield size={20} />,    label: t("sidebar.admin"), description: t("sidebar.admin_description") }]
      : []),
  ];

  const isMobile = windowWidth < 768;

  return (
    <>
      {/* === Mobile: full-screen drop-down menu ============================
          On mobile the sidebar is no longer a slide-in panel — it's a full
          viewport overlay that drops down from the topbar. Includes the
          nav items at the top and the user's profile + Settings + Logout
          at the bottom (the profile dropdown was removed from the mobile
          topbar). */}
      {isMobile && (
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ opacity: 0, y: -16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ type: "tween", duration: 0.18, ease: "easeOut" }}
              className="fixed left-0 right-0 bottom-0 bg-white shadow-2xl z-40 flex flex-col overflow-hidden"
              style={{ top: topBarHeight }}
              role="dialog"
              aria-modal="true"
              aria-label={t("sidebar.menu_aria_label")}
            >
              {/* Nav items — sized for one-screen fit on most phones while
                  keeping the one-line subtitle visible for context. */}
              <nav className="flex-1 pt-3 pb-2 px-3 overflow-y-auto scrollbar-thin space-y-0.5">
                {menuItems.map((item, index) => (
                  <Link
                    key={item.label || index}
                    to={item.path}
                    onClick={() => toggleSidebar(false)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-black hover:bg-gray-50 active:bg-gray-100 transition-colors"
                    style={location.pathname === item.path ? { background: `${orgTheme.primaryColor}1f`, fontWeight: 600 } : undefined}
                  >
                    <div className="relative flex-shrink-0" style={{ color: orgTheme.primaryColor }}>
                      {React.cloneElement(item.icon, { size: 20 })}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium leading-tight">{item.label}</div>
                      {item.description && (
                        <div className="text-[11px] text-gray-500 leading-tight mt-0.5 truncate">
                          {item.description}
                        </div>
                      )}
                    </div>
                    {item.badge > 0 && (
                      <span className="min-w-[20px] h-[20px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1.5 flex-shrink-0">
                        {item.badge > 99 ? "99+" : item.badge}
                      </span>
                    )}
                  </Link>
                ))}
              </nav>

              {/* Profile section pinned to bottom — replaces the desktop
                  topbar profile dropdown on mobile. */}
              <MobileProfileSection
                user={user}
                logout={logout}
                onNavigate={() => toggleSidebar(false)}
                themeColor={orgTheme.primaryColor}
              />
            </motion.div>
          )}
        </AnimatePresence>
      )}

      {/* === Desktop: side panel (unchanged) ================================ */}
      {!isMobile && (
        <motion.div
          initial={false}
          animate={{ x: 0 }}
          transition={{ type: "tween", duration: 0.2, ease: "easeOut" }}
          className={`fixed left-0 bg-white shadow-md z-40
                      flex flex-col transition-[width] duration-300 ease-in-out
                      ${isExpanded ? "w-64" : "w-20"}`}
          style={{ top: topBarHeight, height: `calc(100vh - ${topBarHeight}px)` }}
        >
          <nav className="flex-1 pt-10 pb-4 px-3 overflow-y-auto scrollbar-thin">
            <TooltipProvider delayDuration={200}>
              {menuItems.map((item, index) => (
                <React.Fragment key={item.label || index}>
                  {item.type === "button" ? (
                    <MenuItem
                      isActive={false}
                      isExpanded={isExpanded}
                      icon={item.icon}
                      label={item.label}
                      description={item.description}
                      onClick={item.onClick}
                      tooltipsEnabled={tooltipsEnabled}
                      themeColor={orgTheme.primaryColor}
                      isForcedOpen={false}
                      forcedLabelActive={!!forcedLabel}
                    />
                  ) : (
                    <MenuItem
                      isActive={location.pathname === item.path}
                      isExpanded={isExpanded}
                      icon={item.icon}
                      label={item.label}
                      description={item.description}
                      to={item.path}
                      badge={item.badge}
                      tooltipsEnabled={tooltipsEnabled}
                      themeColor={orgTheme.primaryColor}
                      isForcedOpen={!isExpanded && forcedLabel === item.label}
                      forcedLabelActive={!!forcedLabel}
                    />
                  )}

                  {item.dividerAfter && (
                    <Separator className="my-4 bg-gray-200" />
                  )}
                </React.Fragment>
              ))}
            </TooltipProvider>
          </nav>
        </motion.div>
      )}
    </>
  );
};


const MobileProfileSection = ({ user, logout, onNavigate, themeColor }) => {
  const { t } = useTranslation("nav");
  const displayName = user?.full_name || user?.name || user?.email || t("sidebar.profile.default_name");

  return (
    <div className="border-t border-gray-200 bg-gray-50">
      <Link
        to="/profile/me"
        onClick={onNavigate}
        className="px-4 pt-4 pb-2 flex items-center gap-3 active:bg-gray-100 transition-colors"
      >
        <UserAvatar
          src={user?.avatar_url}
          name={displayName}
          className="w-12 h-12 rounded-full object-cover shadow-sm"
          style={{ border: `2px solid ${themeColor}` }}
          textClassName="text-sm"
        />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900 truncate">{displayName}</p>
          {user?.email && <p className="text-xs text-gray-500 truncate">{user.email}</p>}
        </div>
      </Link>
      <div className="grid grid-cols-2 gap-2 px-4 pb-4 pt-2">
        <Link
          to="/profile/me"
          onClick={onNavigate}
          className="flex items-center justify-center gap-2 py-3 rounded-2xl bg-white border border-gray-200 hover:bg-gray-100 active:bg-gray-200 transition-colors text-sm font-medium text-gray-800"
        >
          <User size={16} style={{ color: themeColor }} />
          {t("sidebar.profile.my_profile")}
        </Link>
        <Link
          to="/settings"
          onClick={onNavigate}
          className="flex items-center justify-center gap-2 py-3 rounded-2xl bg-white border border-gray-200 hover:bg-gray-100 active:bg-gray-200 transition-colors text-sm font-medium text-gray-800"
        >
          <Settings size={16} style={{ color: themeColor }} />
          {t("sidebar.profile.settings")}
        </Link>
        <button
          type="button"
          onClick={() => {
            onNavigate && onNavigate();
            logout && logout();
          }}
          className="col-span-2 flex items-center justify-center gap-2 py-3 rounded-2xl text-white text-sm font-medium shadow-sm hover:opacity-90 active:opacity-80 transition-opacity"
          style={{ background: themeColor }}
        >
          <LogOut size={16} />
          {t("sidebar.profile.sign_out")}
        </button>
      </div>
    </div>
  );
};

const MenuItem = ({ isActive, isExpanded, icon, label, description, to, onClick, badge, tooltipsEnabled, themeColor, isForcedOpen, forcedLabelActive }) => {
  const orgTheme = useContext(OrgThemeContext);
  const accent = orgTheme.primaryColor;
  const ItemContent = () => (
    <div
      className={`flex items-center rounded-2xl p-3 transition-colors duration-75 text-black
                 ${isActive ? "font-semibold" : "hover:bg-gray-100"}`}
      style={isActive ? { background: `${accent}40` } : undefined}
    >
      <div className="relative shrink-0" style={{ color: accent }}>
        {icon}
        {!isExpanded && badge > 0 && (
          <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-[16px] bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center px-0.5">
            {badge > 99 ? "99+" : badge}
          </span>
        )}
      </div>

      {isExpanded && (
        <span className="ml-3 flex-1 min-w-0 text-black whitespace-normal break-words leading-snug">
          {label}
        </span>
      )}

      {isExpanded && badge > 0 && (
        <span className="min-w-[20px] h-[20px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </div>
  );

  // When collapsed, tooltip shows the label (it's the only place the user sees it).
  // When expanded, tooltip shows the longer description on hover so users learn what each item does.
  const collapsedTooltip = !isExpanded && (isForcedOpen || (tooltipsEnabled && !forcedLabelActive));
  const expandedTooltip = isExpanded && !!description;
  const showTooltipContent = collapsedTooltip || expandedTooltip;
  const tooltipText = isExpanded ? description : label;

  if (to) {
    return (
      <Tooltip open={isForcedOpen ? true : undefined}>
        <TooltipTrigger asChild>
          <Link to={to} className="block relative">
            <ItemContent />
          </Link>
        </TooltipTrigger>
        {showTooltipContent && (
          <TooltipContent side="right" color={themeColor} className="max-w-[220px] whitespace-normal text-center">
            {tooltipText}
          </TooltipContent>
        )}
      </Tooltip>
    );
  }

  return (
    <Tooltip open={isForcedOpen ? true : undefined}>
      <TooltipTrigger asChild>
        <button onClick={onClick} className="block w-full text-left relative">
          <ItemContent />
        </button>
      </TooltipTrigger>
      {showTooltipContent && (
        <TooltipContent side="right" color={themeColor} className="max-w-[220px] whitespace-normal text-center">
          {tooltipText}
        </TooltipContent>
      )}
    </Tooltip>
  );
};

export default Sidebar;
